import { STORAGE_KEYS } from "@/config/constants";
import { getBrowserCookieValue } from "@/lib/auth/cookies";
import { resolveGrowthBookClientId } from "@/lib/integrations/growthbook-cookie";

// fbclid comes from getMetaMatchParams() below, shared with the conversion-header path in client.ts
// salesforce_uuid is the speakai.co web-session token ("web:<session_id>"), captured
// first-touch by captureAttributionFromUrl() alongside the click ids.
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "gclid", "gbraid", "wbraid", "salesforce_uuid"] as const;

// Meta pixel identifiers live in document.cookie (not localStorage) and the leading underscore
// is dropped so the payload field matches the server register allowlist (`_fbp` -> `fbp`).
const FB_COOKIE_MAP: Record<string, string> = { _fbp: "fbp", _fbc: "fbc" };

// First-touch referrer keys captured by captureAttributionFromUrl() on mount. The localStorage key names are
// inverted relative to their contents — this is the canonical mapping, do not "correct" it:
//   ga_original_landing_page = document.referrer (external site arrived FROM)  -> field `referer`
//   ga_original_referrer     = first in-app URL landed ON (e.g. /auth/register) -> field `landing_url`
// Both are query-stripped + 200-char-capped at capture time.
const REFERRER_KEY_MAP: Record<string, string> = {
  [STORAGE_KEYS.GA_ORIGINAL_LANDING_PAGE]: "referer",
  [STORAGE_KEYS.GA_ORIGINAL_REFERRER]: "landing_url",
};

// Marketing-site attribution passed explicitly by speakai.co on register CTAs and
// captured first-touch by captureAttributionFromUrl(). These describe acquisition — which page brought the
// visitor in, and which page they converted on — whereas REFERRER_KEY_MAP above
// describes in-app arrival. Applied after it so a real marketing value wins.
const MARKETING_KEY_MAP: Record<string, string> = {
  [STORAGE_KEYS.MARKETING_LANDING_URL]: "landing_url",
  [STORAGE_KEYS.MARKETING_CONVERSION_PAGE]: "conversion_page",
};

/**
 * Validate + normalize a marketing attribution URL supplied via query string
 * (`landing_url` / `conversion_page` on speakai.co register CTAs).
 *
 * Returns the normalized `origin + pathname`, or undefined when the value is unusable —
 * callers MUST drop an undefined result. Mirrors the server's sanitizeUtmUrlField so the
 * two trust boundaries agree: https only, no HTML metacharacters, CR/LF stripped, query
 * and fragment dropped, 500-char cap. Unparseable values are rejected outright rather
 * than falling back to raw string slicing.
 */
export function sanitizeMarketingUrl(raw: unknown): string | undefined {
  if (typeof raw !== "string" || raw.length === 0 || raw.length > 500) return undefined;
  const cleaned = raw.replace(/[\r\n\t]/g, "");
  if (/[<>"']/.test(cleaned)) return undefined;
  try {
    const u = new URL(cleaned);
    if (u.protocol !== "https:") return undefined;
    return `${u.origin}${u.pathname}`.slice(0, 500);
  } catch {
    return undefined;
  }
}

/**
 * Live Meta pixel match params — `_fbp`/`_fbc` cookies (leading underscore dropped)
 * and `fbclid` from localStorage. Read fresh at call time (not a page-load snapshot)
 * so conversion-endpoint headers (client.ts) always carry the current cookie state.
 */
export function getMetaMatchParams(): { fbp?: string; fbc?: string; fbclid?: string } {
  try {
    const result: { fbp?: string; fbc?: string; fbclid?: string } = {};
    for (const [cookieName, field] of Object.entries(FB_COOKIE_MAP)) {
      const v = getBrowserCookieValue(cookieName);
      if (v) result[field as "fbp" | "fbc"] = v;
    }
    const fbclid = localStorage.getItem("fbclid");
    if (fbclid) result.fbclid = fbclid;
    // ?fbc= from speakai.co (cross-domain _fbc forwarded via speak-attribution.js) carries the real click timestamp, so it wins over a locally-synthesized fbc.
    if (!result.fbc) {
      const storedFbc = localStorage.getItem("fbc");
      if (storedFbc) result.fbc = storedFbc;
    }
    // Meta's official fbc format when no _fbc cookie has been set yet by the pixel
    // (e.g. pixel not loaded, ad blocker) but the visitor arrived via a Meta ad click.
    // fbclid is validated at capture time (captureAttributionFromUrl below) before it ever
    // reaches localStorage, so it's safe to interpolate here.
    if (!result.fbc && fbclid) {
      result.fbc = `fb.1.${Date.now()}.${fbclid}`;
    }
    return result;
  } catch {
    return {};
  }
}

/**
 * First-touch acquisition attribution for the register / SSO payload — the UTM/click-id
 * params plus `referer`, `landing_url` and `conversion_page`. Returns undefined when
 * nothing was captured.
 */
export function getAttributionParams(): Record<string, string> | undefined {
  try {
    const collected: Record<string, string> = {};
    for (const k of UTM_KEYS) {
      const v = localStorage.getItem(k);
      if (v) collected[k] = v;
    }
    for (const [lsKey, field] of Object.entries(REFERRER_KEY_MAP)) {
      const v = localStorage.getItem(lsKey);
      if (v) collected[field] = v;
    }
    // Assigned after REFERRER_KEY_MAP so an explicit speakai.co landing_url replaces the
    // in-app default (which is our own /auth/register page, not an acquisition source).
    for (const [lsKey, field] of Object.entries(MARKETING_KEY_MAP)) {
      const v = localStorage.getItem(lsKey);
      if (v) collected[field] = v;
    }
    const { fbp, fbc, fbclid } = getMetaMatchParams();
    if (fbp) collected.fbp = fbp;
    if (fbc) collected.fbc = fbc;
    if (fbclid) collected.fbclid = fbclid;
    const gb = resolveGrowthBookClientId();
    if (gb.source === "ga" && gb.id) collected.ga_client_id = gb.id;
    return Object.keys(collected).length > 0 ? collected : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Store first-touch acquisition params from the landing URL (UTMs, click ids, referrer,
 * speakai.co landing/conversion pages) in localStorage. Never overwrites an existing value.
 */
export function captureAttributionFromUrl(): void {
  try {
    const p = new URLSearchParams(window.location.search);
    const keys = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
    const has = keys.some((k) => p.has(k));
    if (has && !localStorage.getItem("utm_source")) {
      keys.forEach((k) => {
        const v = p.get(k);
        if (v) localStorage.setItem(k, v.slice(0, 200));
      });
    }
    // fbclid is later used to construct the _fbc match param (utm.ts getMetaMatchParams) —
    // validate it's a plausible click-id token before it ever reaches localStorage.
    const FBCLID_ALLOWLIST = /^[A-Za-z0-9_.-]{1,200}$/;
    // Meta's documented fbc shape: fb.<subdomainIndex>.<creationTime>.<fbclid>, forwarded by speak-attribution.js from the speakai.co _fbc cookie.
    const FBC_ALLOWLIST = /^fb\.\d+\.\d+\.[A-Za-z0-9_.-]{1,200}$/;
    // speakai.co web-session token; same shape as the server allowlist; distinct from the userId payload calendly.ts stamps under this param name
    const SALESFORCE_UUID_ALLOWLIST = /^web:[A-Za-z0-9_-]{1,59}$/;
    const clickIds = ["gclid", "gbraid", "wbraid", "fbclid", "fbc", "salesforce_uuid"];
    clickIds.forEach((k) => {
      const v = p.get(k);
      if (!v || localStorage.getItem(k)) return;
      if ((k === "fbclid" || k === "gbraid" || k === "wbraid") && !FBCLID_ALLOWLIST.test(v)) return;
      if (k === "fbc" && !FBC_ALLOWLIST.test(v)) return;
      if (k === "salesforce_uuid" && !SALESFORCE_UUID_ALLOWLIST.test(v)) return;
      localStorage.setItem(k, v.slice(0, 200));
    });
    const stripQuery = (url: string) => {
      try {
        const u = new URL(url);
        if (!["http:", "https:"].includes(u.protocol)) return "";
        return u.origin + u.pathname;
      } catch {
        return url.split("?")[0].split("#")[0];
      }
    };
    if (!localStorage.getItem("ga_original_landing_page") && document.referrer) {
      localStorage.setItem("ga_original_landing_page", stripQuery(document.referrer).slice(0, 200));
    }
    if (!localStorage.getItem("ga_original_referrer")) {
      localStorage.setItem("ga_original_referrer", stripQuery(window.location.href).slice(0, 200));
    }
    // Marketing-site attribution speakai.co passes explicitly on register CTAs.
    // Neither can be recovered in-app: cross-origin Referer is truncated to the bare
    // origin, and ga_original_referrer is by definition the first in-app URL.
    // First-touch — never overwritten, so browsing the app cannot clobber the origin.
    (
      [
        ["landing_url", STORAGE_KEYS.MARKETING_LANDING_URL],
        ["conversion_page", STORAGE_KEYS.MARKETING_CONVERSION_PAGE],
      ] as const
    ).forEach(([param, storageKey]) => {
      const v = p.get(param);
      if (!v || localStorage.getItem(storageKey)) return;
      const clean = sanitizeMarketingUrl(v);
      if (clean) localStorage.setItem(storageKey, clean);
    });
  } catch {
    // Best-effort: UTM capture failure is non-fatal
  }
}
